import React, { useState } from 'react';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';
import { tr } from '../utils/i18n';
import { routePath } from '../i18n/routes';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const navigateToHomeSection = (sectionId) => {
    if (location.pathname === '/') {
      // Si on est déjà sur la home, scroll vers la section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Si on est sur une autre page, naviguer vers home + scroll vers section
      navigate(routePath('home', currentLanguage), { state: { scrollTo: sectionId } });
    }
    setIsMenuOpen(false);
  };

  const navigateToHome = () => {
    navigate(routePath('home', currentLanguage));
    setTimeout(() => window.scrollTo(0, 0), 100);
    setIsMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0 cursor-pointer" onClick={navigateToHome}>
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_traffic-partner/artifacts/hrx2swuq_logo2%20%281%29.png" 
                alt="OptimHub" 
                className="h-8 w-auto"
                onError={(e) => {
                  // Fallback si l'image ne charge pas
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              <div className="hidden w-8 h-8 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg items-center justify-center">
                <span className="text-white font-bold text-lg">O</span>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => navigateToHomeSection('services')}
              className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium"
            >
              {t('services')}
            </button>
            <button
              onClick={() => navigateToHomeSection('benefits')}
              className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium"
            >
              {t('benefits')}
            </button>
            <button
              onClick={() => navigateToHomeSection('testimonials')}
              className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium"
            >
              {t('testimonials')}
            </button>
            <button
              onClick={() => navigateToHomeSection('integrations')}
              className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium"
            >
              {t('integrations')}
            </button>
            <button
              onClick={() => navigateToHomeSection('contact')}
              className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium"
            >
              {t('contact')}
            </button>
            <button
              data-testid="header-publishers-link"
              onClick={() => {
                navigate(routePath('publishers', currentLanguage));
                setIsMenuOpen(false);
              }}
              className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium"
            >
              {tr(currentLanguage, 'Éditeurs')}
            </button>
          </nav>

          {/* Language Switcher & CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            <a 
              href={routePath('onePager', currentLanguage)}
              className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              PDF
            </a>
            <LanguageSwitcher />
            <Button
              onClick={() => navigateToHomeSection('contact')}
              className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-6 py-2 transition-all duration-300 transform hover:scale-105"
            >
              {t('requestDemo')}
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-2">
            <LanguageSwitcher />
            <button
              onClick={toggleMenu}
              className="text-gray-700 hover:text-blue-600 focus:outline-none focus:text-blue-600 transition-colors duration-200"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-100">
              <button
                onClick={() => navigateToHomeSection('services')}
                className="block w-full text-left px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors duration-200"
              >
                {t('services')}
              </button>
              <button
                onClick={() => navigateToHomeSection('benefits')}
                className="block w-full text-left px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors duration-200"
              >
                {t('benefits')}
              </button>
              <button
                onClick={() => navigateToHomeSection('testimonials')}
                className="block w-full text-left px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors duration-200"
              >
                {t('testimonials')}
              </button>
              <button
                onClick={() => navigateToHomeSection('integrations')}
                className="block w-full text-left px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors duration-200"
              >
                {t('integrations')}
              </button>
              <button
                onClick={() => navigateToHomeSection('contact')}
                className="block w-full text-left px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors duration-200"
              >
                {t('contact')}
              </button>
              <button
                data-testid="header-publishers-link-mobile"
                onClick={() => {
                  navigate(routePath('publishers', currentLanguage));
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors duration-200"
              >
                {tr(currentLanguage, 'Éditeurs')}
              </button>
              <div className="px-3 py-2">
                <Button
                  onClick={() => navigateToHomeSection('contact')}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white"
                >
                  {t('requestDemo')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;